import { useEffect, useRef, useState } from 'react';
import { useActivePractice, useDispatch } from '@witmetrics/data-store';
import { mapToId } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { formatNumber } from '@witmetrics/utils/strings';
import { useToggle } from '@/hooks/useToggle';
import { useNumber } from '@/hooks/useNumber';
import { useIds } from '@/hooks/useIds';
import { usePath } from '@/hooks/usePath';
import { useAppState } from '@/providers/AppStateProvider';
import { PAGES } from '@/constants/pages';
import OverviewCard, { OverviewTab } from '@/components/OverviewCard';
import ProjectsList from '@/components/ProjectsList';
import EmptyMessage from '../EmptyMessage';
import { PROJECT_TAB, type ProjectTab, TAB_FILTERS } from './constants';
import { classes, fetchProjectsForTab } from './utils';

export default function ProjectsCard() {
  const requestRef = useRef(0);
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const activePractice = useActivePractice();
  const { navigate } = usePath();
  const [activeTab, setActiveTab] = useState<ProjectTab>(PROJECT_TAB.ALL);
  const [projectIds, setProjectIds, { clear: clearProjectIds }] = useIds();
  const [rowCount, setRowCount, clearRowCount] = useNumber(0);
  const [isFetchingData, toggleFetchingData] = useToggle(true);

  useEffect(() => {
    if (activePractice) fetchData();
  }, [activePractice, activeTab]);

  const fetchData = async () => {
    // Tag this fetch; a newer tab switch bumps the ref and supersedes it
    const requestId = ++requestRef.current;
    try {
      toggleFetchingData(true);
      clearRowCount();
      clearProjectIds();
      const data = await fetchProjectsForTab(activeTab);
      if (requestRef.current !== requestId) return;
      dispatch.addProjects(data.results);
      setProjectIds(mapToId(data.results));
      setRowCount(data.pagination.rowCount);
      toggleFetchingData(false);
    } catch (err) {
      if (requestRef.current !== requestId) return;
      toggleFetchingData(false);
      onApiError(err, 'Error fetching cases', () => fetchData());
    }
  };

  const handleViewAll = () => {
    navigate(PAGES.CASES, {
      filters: JSON.stringify([TAB_FILTERS[activeTab]]),
    });
  };

  const renderSubtitle = () => {
    if (isFetchingData) return null;
    if (rowCount === 0) return 'No active cases';
    return `${formatNumber(rowCount)} total`;
  };

  const renderList = () => {
    if (isFetchingData || !isEmpty(projectIds)) {
      return (
        <ProjectsList
          showUser
          isFetchingData={isFetchingData}
          className={classes.list}
          projectIds={projectIds}
        />
      );
    }
    return <EmptyMessage>No active cases.</EmptyMessage>;
  };

  return (
    <OverviewCard
      name="active-cases"
      title="Active cases"
      subtitle={renderSubtitle()}
      defaultTab={PROJECT_TAB.ALL}
      onTabChange={(tab) => setActiveTab(tab as ProjectTab)}
      onViewAll={handleViewAll}>
      <OverviewTab name={PROJECT_TAB.ALL} label="All">
        {renderList()}
      </OverviewTab>
      <OverviewTab name={PROJECT_TAB.IN_PROGRESS} label="In Progress">
        {renderList()}
      </OverviewTab>
      <OverviewTab name={PROJECT_TAB.PENDING_APPROVAL} label="Pending Approval">
        {renderList()}
      </OverviewTab>
    </OverviewCard>
  );
}
